// frontend/src/modules/usuarios/pages/UsuariosEmpresa.js
import React, { useState, useEffect } from 'react';
import { FaUserPlus, FaEdit, FaTrash, FaUser, FaEnvelope, FaPhone, FaCheck, FaTimes } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

function UsuariosEmpresa({ empresaId }) {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    nome_completo: '',
    email: '',
    senha: '',
    perfil: 'USUARIO',
    role: 'USER',
    ativo: true
  });

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const token = localStorage.getItem('token');
  const idEmpresa = empresaId || 1;

  // ============================================
  // BUSCAR USUÁRIOS
  // ============================================
  const fetchUsuarios = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/usuarios/empresa/${idEmpresa}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('📦 Usuários:', data.length);
      setUsuarios(data);
    } catch (err) {
      console.error('❌ Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuarios();
  }, [idEmpresa]);

  // ============================================
  // CRIAR/ATUALIZAR USUÁRIO
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const url = editando 
        ? `/api/usuarios/${editando.id}`
        : '/api/usuarios';
      
      const method = editando ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        empresa_id: idEmpresa
      };

      if (!editando && !formData.senha) {
        throw new Error('Senha é obrigatória para novo usuário');
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao salvar');
      }

      setSuccess(editando ? '✅ Usuário atualizado!' : '✅ Usuário criado!');
      setMostrarForm(false);
      setEditando(null);
      resetForm();
      fetchUsuarios();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // EXCLUIR USUÁRIO
  // ============================================
  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este usuário?')) return;
    try {
      const response = await fetch(`/api/usuarios/${id}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao excluir');
      }

      setSuccess('✅ Usuário excluído!');
      fetchUsuarios();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (usuario) => {
    setEditando(usuario);
    setFormData({
      nome_completo: usuario.nome_completo,
      email: usuario.email,
      senha: '',
      perfil: usuario.perfil || 'USUARIO',
      role: usuario.role || 'USER',
      ativo: usuario.ativo !== undefined ? usuario.ativo : true
    });
    setMostrarForm(true);
  };

  const resetForm = () => {
    setFormData({
      nome_completo: '',
      email: '',
      senha: '',
      perfil: 'USUARIO',
      role: 'USER',
      ativo: true
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando usuários...</div>;

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Cabeçalho */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>👥 Usuários da Empresa</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: isMobile ? '13px' : '14px' }}>
            Gerencie os usuários que têm acesso ao sistema
          </p>
        </div>
        <button
          onClick={() => {
            setMostrarForm(!mostrarForm);
            if (!mostrarForm) setEditando(null);
            resetForm();
          }}
          style={{
            padding: isMobile ? '8px 16px' : '10px 20px',
            backgroundColor: mostrarForm ? '#dc3545' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'center',
            fontSize: isMobile ? '13px' : '14px',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          {mostrarForm ? <><FaTimes /> Cancelar</> : <><FaUserPlus /> Novo Usuário</>}
        </button>
      </div>

      {/* Mensagens */}
      {error && (
        <div style={{ backgroundColor: '#ffebee', padding: isMobile ? '10px 15px' : '12px 20px', borderRadius: '4px', color: '#c62828', marginBottom: '15px', border: '1px solid #ffcdd2', fontSize: isMobile ? '13px' : '14px' }}>
          ❌ {error}
          <button 
            onClick={fetchUsuarios}
            style={{ marginLeft: '15px', padding: '4px 12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '12px' : '13px' }}
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {success && (
        <div style={{ backgroundColor: '#e8f5e9', padding: isMobile ? '10px 15px' : '12px 20px', borderRadius: '4px', color: '#2e7d32', marginBottom: '15px', border: '1px solid #c8e6c9', fontSize: isMobile ? '13px' : '14px' }}>
          {success}
        </div>
      )}

      {/* Formulário */}
      {mostrarForm && (
        <form onSubmit={handleSubmit} style={{ backgroundColor: '#f8f9fa', padding: isMobile ? '15px' : '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #dee2e6' }}>
          <h3 style={{ marginTop: 0, fontSize: isMobile ? '16px' : '18px' }}>{editando ? '✏️ Editar Usuário' : '📝 Novo Usuário'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '10px' : '15px' }}>
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Nome Completo:*</label>
              <input
                type="text"
                name="nome_completo"
                value={formData.nome_completo}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Email:*</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>{editando ? 'Nova Senha (opcional)' : 'Senha:*'}</label>
              <input
                type="password"
                name="senha"
                value={formData.senha}
                onChange={handleChange}
                required={!editando}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Perfil:</label>
              <select
                name="perfil"
                value={formData.perfil}
                onChange={handleChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              >
                <option value="USUARIO">👤 Usuário</option>
                <option value="ADMIN">👑 Administrador</option>
                <option value="GERENTE">📊 Gerente</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Role:</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}
              >
                <option value="USER">Usuário</option>
                <option value="ADMIN">Admin</option>
                <option value="SUPER_ADMIN">Super Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ marginRight: '10px', fontSize: isMobile ? '13px' : '14px' }}>Ativo:</label>
              <input
                type="checkbox"
                name="ativo"
                checked={formData.ativo}
                onChange={handleChange}
                style={{ width: '20px', height: '20px' }}
              />
            </div>
          </div>
          <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button type="submit" disabled={loading} style={{ 
              padding: isMobile ? '8px 20px' : '10px 30px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              flex: isMobile ? '1' : '0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center'
            }}>
              {loading ? 'Salvando...' : <><FaCheck /> Salvar</>}
            </button>
            <button type="button" onClick={() => { setMostrarForm(false); setEditando(null); resetForm(); }} style={{ 
              padding: isMobile ? '8px 20px' : '10px 30px', 
              backgroundColor: '#6c757d', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              fontSize: isMobile ? '13px' : '14px',
              flex: isMobile ? '1' : '0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'center'
            }}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Lista de Usuários */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '10px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        {usuarios.length === 0 ? (
          <div style={{ textAlign: 'center', padding: isMobile ? '30px' : '40px' }}>
            <div style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: '10px' }}>👥</div>
            <p style={{ color: '#6c757d', fontSize: isMobile ? '13px' : '14px' }}>Nenhum usuário encontrado.</p>
          </div>
        ) : isMobile ? (
          // ============================================
          // MOBILE - CARDS
          // ============================================
          <div>
            {usuarios.map((usuario) => (
              <div
                key={usuario.id}
                style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '10px',
                  borderLeft: `4px solid ${usuario.ativo ? '#28a745' : '#dc3545'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <strong style={{ fontSize: '14px' }}>
                    <FaUser style={{ marginRight: '6px', color: '#6c757d', fontSize: '12px' }} />
                    {usuario.nome_completo}
                  </strong>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: usuario.ativo ? '#d4edda' : '#f8d7da',
                    color: usuario.ativo ? '#155724' : '#721c24',
                    fontSize: '10px'
                  }}>
                    {usuario.ativo ? '✅ Ativo' : '❌ Inativo'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6c757d' }}>
                  <FaEnvelope style={{ fontSize: '11px' }} />
                  <span>{usuario.email}</span>
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: usuario.perfil === 'ADMIN' ? '#cce5ff' : '#e9ecef',
                    color: usuario.perfil === 'ADMIN' ? '#004085' : '#6c757d',
                    fontSize: '10px'
                  }}>
                    {usuario.perfil || 'USUARIO'}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    backgroundColor: usuario.role === 'SUPER_ADMIN' ? '#f8d7da' : '#d4edda',
                    color: usuario.role === 'SUPER_ADMIN' ? '#721c24' : '#155724',
                    fontSize: '10px'
                  }}>
                    {usuario.role || 'USER'}
                  </span>
                </div>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
                  <button onClick={() => handleEdit(usuario)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer' }} title="Editar">
                    <FaEdit size={14} />
                  </button>
                  <button onClick={() => handleDelete(usuario.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }} title="Excluir">
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // ============================================
          // DESKTOP - TABELA
          // ============================================
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>ID</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Nome</th>
                <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Perfil</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Role</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map((usuario) => (
                <tr key={usuario.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '10px' }}>{usuario.id}</td>
                  <td style={{ padding: '10px' }}><FaUser /> {usuario.nome_completo}</td>
                  <td style={{ padding: '10px' }}><FaEnvelope /> {usuario.email}</td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: usuario.perfil === 'ADMIN' ? '#cce5ff' : '#e9ecef',
                      color: usuario.perfil === 'ADMIN' ? '#004085' : '#6c757d'
                    }}>
                      {usuario.perfil || 'USUARIO'}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: usuario.role === 'SUPER_ADMIN' ? '#f8d7da' : '#d4edda',
                      color: usuario.role === 'SUPER_ADMIN' ? '#721c24' : '#155724'
                    }}>
                      {usuario.role || 'USER'}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: '12px',
                      backgroundColor: usuario.ativo ? '#d4edda' : '#f8d7da',
                      color: usuario.ativo ? '#155724' : '#721c24'
                    }}>
                      {usuario.ativo ? '✅ Ativo' : '❌ Inativo'}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center' }}>
                    <button onClick={() => handleEdit(usuario)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', marginRight: '10px' }} title="Editar">
                      <FaEdit />
                    </button>
                    <button onClick={() => handleDelete(usuario.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }} title="Excluir">
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default UsuariosEmpresa;